import React, { useState } from 'react'

import { Button, Dialog, DialogTitle, DialogActions, CircularProgress } from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'
import StyledTextField from '../../Common/StyledTextField'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import useCurrentUser from '../../../hooks/useCurrentUser'
import StyledAlert from '../../Common/StyledAlert'

const useStyles = makeStyles(() => ({
	title: {
		marginBottom: 24
	},
	dialogActions: {
		justifyContent: 'flex-start'
	},
	fieldWrapper: {
		minWidth: '400px !important',
		width: '400px !important',
		marginBottom: '1.75rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '.75rem'
	}
}))

const AddDaypassCreditDialog = ({ isShown, setIsShown, adding }) => {
	const classes = useStyles()
	const [numberOfDaypassesToAdd, setNumberOfDaypassesToAdd] = useState(1)
	const [numberOfDaypassesToUse, setNumberOfDaypassesToUse] = useState(1)
	const [numberOfDogs, setNumberOfDogs] = useState(4)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const { household, addDaypassesToHousehold, useDaypassesFromHousehold } = useSelectedHousehold()
	const { facility } = useSelectedFacility()
	const { user, isBackOfficeUser } = useCurrentUser()

	const word = `${adding ? 'ADD' : 'REMOVE'} DAYPASS`
	const day_pass_price = facility?.day_pass_price || 0

	const addDaypasses = async () => {
		if (!household || !facility) {
			return
		}
		if (!isBackOfficeUser) {
			setError('Only back office users can add daypasses.')
			return
		}
		setError('')
		setLoading(true)
		try {
			const payload = {
				household_id: household?._id,
				number_of_passes: numberOfDaypassesToAdd,
				number_of_dogs: numberOfDogs,
				staff_uid: user.uid,
				day_pass_price,
				manual_discount: {
					amount: -day_pass_price,
					description: 'Fully Discounted Daypass Added via FA'
				},
				client_purchased: false,
				total: 0 * day_pass_price // these are free currently
			}
			const result = await addDaypassesToHousehold(payload)
			if (result.success) {
				closeDialog()
				setLoading(false)
				return
			}
			if (result.errors && result.errors.length) {
				setError(result.errors[0])
			}
		} catch (e) {
			setError(e.message || 'Error creating daypasses.')
		}
		setLoading(false)
	}

	const removeDaypasses = async () => {
		if (!household || !facility) {
			return
		}
		setError('')
		setLoading(true)
		try {
			const payload = {
				household_id: household?._id,
				number_to_use: numberOfDaypassesToUse
			}
			const result = await useDaypassesFromHousehold(payload)
			if (result.success) {
				closeDialog()
				setLoading(false)
				return
			}
			if (result.errors && result.errors.length) {
				setError(result.errors[0])
			}
		} catch (e) {
			setError(e.message || 'Error creating daypasses.')
		}
		setLoading(false)
	}

	const closeDialog = () => {
		setIsShown(false)
	}

	return (
		<Dialog
			open={isShown}
			aria-labelledby="add-daypass-dialog"
			onClose={closeDialog}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle className={classes.title}>
				{adding ? 'Add Daypass to Household' : 'Remove Daypass from Household'}
			</DialogTitle>
			<div>
				{adding ? (
					<div className={classes.fieldWrapper}>
						<StyledTextField
							variant="filled"
							value={numberOfDaypassesToAdd}
							disabled
							label="Number of daypasses"
						/>
						<StyledTextField variant="filled" disabled value={numberOfDogs} label="Number of dogs" />
						{error > '' ? <StyledAlert type="error" text={error} /> : null}
					</div>
				) : (
					<div className={classes.fieldWrapper}>
						<StyledTextField
							variant="filled"
							disabled
							value={numberOfDaypassesToUse}
							label="Number to remove"
						/>
						{error > '' ? <StyledAlert type="error" text={error} /> : null}
					</div>
				)}
			</div>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					color="secondary"
					onClick={adding ? addDaypasses : removeDaypasses}
					disabled={loading}>
					{loading ? <CircularProgress /> : word}
				</Button>
				<Button variant="outlined" color="secondary" onClick={closeDialog} disabled={loading}>
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default AddDaypassCreditDialog
